import { Trans, useLingui } from "@lingui/macro";
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import type { Problem } from "@app/utils/problem-list";
import { valueLabel, type ValueLabels } from "@app/utils/value-labels";
import { PassageRow } from "@app/components/ValuePassages";

/** Accent-folded, lower-cased form — the same tolerance the backend applies before
 *  comparing two spellings. The status arrives already translated ("résolu", "nié"),
 *  but the stored vocabulary is ASCII and a value that skips the read path would
 *  otherwise fall through to the default and be shown as an active problem. */
function fold(value?: string): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/** The five states a reality can be in, and how each reads at a glance.
 *
 *  This mirrors ``ClinicalEntity.ENTITY_STATUSES`` (actif, resolu, nie, suspecte,
 *  planifie) — a closed vocabulary the backend re-derives AT the slider date. The
 *  pill used to be binary: anything that was not "résolu" was painted green and
 *  labelled "Actif", so a reality the record explicitly DENIES was shown to a
 *  surgeon as an active clinical problem, and a merely planned act as a current one.
 *
 *  "Nié" is deliberately not styled like "Résolu" even though both are inactive:
 *  a resolved problem happened and is over, a denied one never held. Same silence
 *  on the page, opposite clinical meaning.
 */
const STATUS_TONES: Record<string, string> = {
  actif: "bg-emerald-50 text-emerald-700",
  resolu: "bg-slate-100 text-slate-500",
  nie: "border border-slate-300 bg-white text-slate-500",
  suspecte: "bg-blue-50 text-blue-900",
  planifie: "bg-primary/10 text-primary",
};

function statusTone(statut?: string): string {
  return STATUS_TONES[fold(statut)] ?? STATUS_TONES.actif;
}

function StatusLabel({ statut }: { statut?: string }) {
  switch (fold(statut)) {
    case "resolu":
      return <Trans>Résolu</Trans>;
    case "nie":
      return <Trans>Nié</Trans>;
    case "suspecte":
      return <Trans>Suspecté</Trans>;
    case "planifie":
      return <Trans>Planifié</Trans>;
    default:
      return <Trans>Actif</Trans>;
  }
}

function formatDay(value?: string): string | null {
  return value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY") : null;
}

function ProblemRow({
  problem,
  labels,
}: {
  problem: Problem;
  labels?: ValueLabels;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const composantes = problem.composantes ?? [];
  const preuves = problem.preuve ?? [];
  const depuis = formatDay(problem.depuis);

  // The projection passes this through honestly — absent means no document dated
  // the fact itself, which is a different thing from "current". Translated here
  // rather than stored translated, so the column stays comparable.
  const nature =
    problem.nature_temporelle === "antecedent"
      ? t`antécédent`
      : problem.nature_temporelle === "planifie"
        ? t`planifié`
        : problem.nature_temporelle === "courant"
          ? t`en cours`
          : null;

  const hasPanel = composantes.length > 0 || preuves.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-start gap-2.5">
        <span
          className={[
            "mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-3xs font-semibold uppercase tracking-wide",
            statusTone(problem.statut),
          ].join(" ")}
        >
          <StatusLabel statut={problem.statut} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900">
            {valueLabel(labels, problem.label)}
          </div>
          {(depuis || nature) && (
            <div className="mt-0.5 text-xs text-slate-500">
              {depuis && <Trans>depuis le {depuis}</Trans>}
              {depuis && nature ? " · " : ""}
              {nature}
            </div>
          )}
          {problem.conteste && (
            <div className="mt-1 inline-flex items-center rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-2xs font-medium text-primary">
              <Trans>Documents en désaccord</Trans>
            </div>
          )}
          {hasPanel && (
            <>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="mt-1 inline-flex items-center gap-1 rounded text-xs font-medium text-primary hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {open ? (
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                )}
                {preuves.length > 0 ? (
                  preuves.length > 1 ? (
                    <Trans>{preuves.length} passages</Trans>
                  ) : (
                    <Trans>1 passage</Trans>
                  )
                ) : composantes.length > 1 ? (
                  <Trans>{composantes.length} détails</Trans>
                ) : (
                  <Trans>1 détail</Trans>
                )}
              </button>
              {open && (
                <div className="mt-1.5 flex flex-col gap-2">
                  {composantes.length > 0 && (
                    <div>
                      <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-slate-400">
                        <Trans>Autres formulations</Trans>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {composantes.map((c, i) => (
                          <span
                            key={i}
                            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-2xs text-slate-600"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {preuves.length > 0 && (
                    <div>
                      <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-slate-400">
                        <Trans>Passages qui l'établissent</Trans>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {preuves.map((p, i) => (
                          <PassageRow
                            key={p.assertion_id ?? i}
                            passage={{
                              key: p.assertion_id ?? String(i),
                              verbatim: p.verbatim ?? "",
                              sourceDocId: p.source_doc_id,
                              date: p.date,
                              docType: p.doc_type,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Two-tier problem list for a synthesised clinical field: major problems with a
 *  status badge, and a collapsible panel carrying the other wordings documents
 *  gave the reality plus the passages that establish it. Read-only — it is a
 *  re-derivable view over the immutable contribution journal, not an editable
 *  value.
 *
 *  Everything it renders is already filtered to the slider position by the
 *  backend projection, `preuve` included: a reader looking at 2023 is never shown
 *  a 2024 letter as proof of what was known then.
 *
 *  A major problem of a field declaring its list is one of its terms, shown through `labels`; its
 *  components are the documents' own wordings and are shown as written. */
export default function ProblemListDisplay({
  problems,
  labels,
}: {
  problems: Problem[];
  labels?: ValueLabels;
}) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      {problems.map((p, i) => (
        <ProblemRow key={i} problem={p} labels={labels} />
      ))}
    </div>
  );
}
